import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useDropzone } from 'react-dropzone'
import { Button } from '@/components/ui/Button'
import { Upload, X, ArrowRight, ArrowLeft, Image, Loader2 } from 'lucide-react'
import type { FormData } from '@/hooks/useSubmissionWizard'
import { cn } from '@/lib/utils'
import { convertHeicToJpeg, isHeicFile } from '@/lib/heic-converter'

interface PhotoStepProps {
  formData: FormData
  updateField: <K extends keyof FormData>(field: K, value: FormData[K]) => void
  onNext: () => void
  onBack: () => void
}

const MAX_SIZE = 50 * 1024 * 1024 // 50MB

export function PhotoStep({ formData, updateField, onNext, onBack }: PhotoStepProps) {
  const [error, setError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isConverting, setIsConverting] = useState(false)

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: unknown[]) => {
      if (rejectedFiles.length > 0) {
        setError('Please upload a valid image file (JPEG, PNG, WebP, or HEIC)')
        return
      }

      let file = acceptedFiles[0]
      if (!file) return

      if (file.size > MAX_SIZE) {
        setError('File must be under 50MB')
        return
      }

      // Convert HEIC to JPEG if needed
      if (isHeicFile(file)) {
        setIsConverting(true)
        try {
          file = await convertHeicToJpeg(file)
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to process HEIC file')
          setIsConverting(false)
          return
        }
        setIsConverting(false)
      }

      // Revoke previous preview URL if exists
      if (formData.photoPreview) {
        URL.revokeObjectURL(formData.photoPreview)
      }

      updateField('photo', file)
      updateField('photoPreview', URL.createObjectURL(file))
      setError(null)
    },
    [formData.photoPreview, updateField]
  )

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.heic', '.heif'],
    },
    maxFiles: 1,
    noClick: !!formData.photoPreview,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    onDropAccepted: () => setIsDragging(false),
    onDropRejected: () => setIsDragging(false),
  })

  const removePhoto = () => {
    if (formData.photoPreview) {
      URL.revokeObjectURL(formData.photoPreview)
    }
    updateField('photo', null)
    updateField('photoPreview', null)
  }

  return (
    <div className="mx-auto max-w-xl space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
        <h2 className="text-foreground font-display text-3xl font-bold md:text-4xl lg:text-5xl">Upload your photo</h2>
        <p className="text-foreground-muted text-lg">Share your best photograph with the world</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <div
          {...getRootProps()}
          className={cn(
            'relative rounded-2xl border-2 border-dashed transition-all duration-300',
            isDragging
              ? 'border-accent bg-accent/5 scale-[1.02]'
              : formData.photoPreview
                ? 'border-transparent'
                : 'border-border hover:border-foreground-subtle cursor-pointer'
          )}
        >
          <input {...getInputProps()} />

          <AnimatePresence mode="wait">
            {isConverting ? (
              <motion.div
                key="converting"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center px-8 py-16 md:py-20"
              >
                <Loader2 className="text-accent mb-4 h-12 w-12 animate-spin" />
                <p className="text-foreground font-medium">Converting HEIC image...</p>
                <p className="text-foreground-subtle text-sm">This may take a moment</p>
              </motion.div>
            ) : formData.photoPreview ? (
              <motion.div
                key="preview"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-surface-hover relative overflow-hidden rounded-2xl"
              >
                <img
                  src={formData.photoPreview}
                  alt="Preview"
                  className="mx-auto max-h-[400px] w-full object-contain"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                <div className="absolute right-4 bottom-4 left-4 flex items-center justify-between">
                  <span className="max-w-[200px] truncate text-sm font-medium text-white">{formData.photo?.name}</span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        open()
                      }}
                      className="rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-colors hover:bg-white/30"
                    >
                      <Image className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        removePhoto()
                      }}
                      className="hover:bg-error/80 rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="dropzone"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center px-8 py-16 md:py-20"
              >
                <motion.div
                  animate={{ y: isDragging ? -10 : 0 }}
                  className="bg-surface-hover mb-6 flex h-16 w-16 items-center justify-center rounded-full"
                >
                  <Upload className="text-foreground-muted h-8 w-8" />
                </motion.div>
                <p className="text-foreground mb-2 text-lg font-medium">Drag and drop your photo</p>
                <p className="text-foreground-subtle mb-4 text-sm">or click to browse</p>
                <p className="text-foreground-subtle text-xs">JPEG, PNG, WebP, HEIC up to 50MB</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {error && (
        <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-error text-sm">
          {error}
        </motion.p>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="flex items-center gap-4"
      >
        <Button type="button" variant="ghost" size="lg" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Button size="lg" disabled={!formData.photo} onClick={onNext} className="flex-1 md:min-w-[200px] md:flex-none">
          Continue
          <ArrowRight className="h-5 w-5" />
        </Button>
      </motion.div>
    </div>
  )
}
